import React, { useContext, useState } from 'react';
import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { UserContext } from '@/components/App';
import { useClientUser } from '@/hooks';
import { PrimaryButton } from '@/styles/blocks';
import { LIST_TARIFFS_BY_CLIENT_ID } from '@/components/Account/Tariffs/graphql';
import { AXIS_OPTIONS, AXIS2_OPTIONS } from '@/components/Account/Tariffs/types/onwardConsolidated';
import {
    AccentBar,
    EmptyState,
    Section,
    SectionHeader,
    SectionNote,
    SectionTitle,
    StatusPill,
} from '@/components/CorporateShipper/blocks';
import { useCarrierRows } from '@/components/CorporateUsers/Carriers/hooks';
import {
    defaultPricingCardFor,
    defaultPricingLegsFor,
    isFulfillmentPartnerRow,
    NO_DEFAULT_PRICING,
} from '@/components/CorporateUsers/Carriers/utils';
import PricingModal from '@/components/CorporateUsers/modals/PricingModal';
import {
    CarrierPill,
    HeaderBadge,
    ListCard,
    ListRow,
    NoticeBody,
    NoticeCard,
    NoticeTag,
    NoticeText,
    NoticeTitle,
    Panel,
    RowDetail,
    RowName,
} from './blocks';
import { shipperTariffLinksFor, TARIFF_SOURCES, unlistedCarrierRowFor } from './utils';

const SOURCE_PILLS = {
    [TARIFF_SOURCES.OWN]: { tone: 'on', label: 'Own tariff' },
    [TARIFF_SOURCES.DEFAULT]: { tone: 'off', label: 'Default' },
    [TARIFF_SOURCES.NONE]: { tone: 'bad', label: 'None' },
};

const DefaultTariff = ({ row, defaultPricing }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const card = defaultPricingCardFor(defaultPricing);

    const tone = card.satisfied ? 'on' : 'bad';
    const tag = card.satisfied ? 'LINKED' : '! NONE';
    const title = card.satisfied ? card.detail : 'No default tariff linked';
    const actionLabel = card.satisfied ? 'Update' : 'Link default tariff';
    const defaultLegs = defaultPricingLegsFor(row);
    let defaultNote = '· prices every org shipper that has no tariff of its own with this carrier';
    let fallbackNote = "shippers with no tariff of their own fall back to this carrier's marketplace rate";
    if (row.isContractor) {
        defaultNote = '· prices every Flex Center that has no tariff of its own with this Motor Carrier';
        fallbackNote = "Flex Centers with no tariff of their own fall back to this Motor Carrier's marketplace rate";
    }

    return (
        <Section>
            <SectionHeader>
                <AccentBar />
                <SectionTitle>Default tariff</SectionTitle>
                <SectionNote>{defaultNote}</SectionNote>
                <HeaderBadge>corporate</HeaderBadge>
            </SectionHeader>
            <NoticeCard tone={tone}>
                <NoticeTag tone={tone}>{tag}</NoticeTag>
                <NoticeText>
                    <NoticeTitle>{title}</NoticeTitle>
                    {!card.satisfied && <NoticeBody>{fallbackNote}</NoticeBody>}
                </NoticeText>
                <PrimaryButton onClick={() => setModalOpen(true)}>{actionLabel}</PrimaryButton>
            </NoticeCard>
            {modalOpen && (
                <PricingModal
                    open
                    onClose={() => setModalOpen(false)}
                    carrier_id={row.client_id}
                    defaultPricing={defaultPricing.overrides}
                    defaultLegs={defaultLegs}
                    forContractor={row.isContractor}
                />
            )}
        </Section>
    );
};

const SelfServiceUploads = () => {
    const { client } = useContext(UserContext);
    const { writableTariffClientIds } = useClientUser();
    // Same variables as the account page's Tariffs tab, so both read one cached result.
    const { data, error } = useQuery(LIST_TARIFFS_BY_CLIENT_ID, {
        variables: { client_ids: writableTariffClientIds },
        onError: (queryError) => Sentry.captureException(queryError),
    });

    let rows = <EmptyState>Loading rate cards…</EmptyState>;
    if (error) rows = <EmptyState>Couldn&apos;t load rate cards.</EmptyState>;
    if (data) {
        rows = data.tariffs.reduce((rateCards, tariff) => {
            if (tariff.client_id !== client.client_id || tariff.tariff_type !== 'ORDER') return rateCards;

            // LTL zone tariffs key on STATE, which the consolidated editor's axis labels don't name.
            const axisLabel = AXIS_OPTIONS[tariff.base_rates_axis] || tariff.base_rates_axis;
            const axis2Label = AXIS2_OPTIONS[tariff.base_rates_axis2];
            const structure = axis2Label ? `${axisLabel} × ${axis2Label}` : axisLabel;
            rateCards.push(
                <ListRow key={tariff.tariff_id}>
                    <RowName>{tariff.name || 'Tariff'}</RowName>
                    <RowDetail>structure: {structure}</RowDetail>
                    <CarrierPill>carrier</CarrierPill>
                </ListRow>
            );

            return rateCards;
        }, []);
    }
    if (data && rows.length === 0) {
        rows = (
            <ListRow>
                <RowDetail>No rate cards uploaded.</RowDetail>
            </ListRow>
        );
    }

    return (
        <Section>
            <SectionHeader>
                <AccentBar />
                <SectionTitle>Their self-service uploads</SectionTitle>
                <SectionNote>· preferred pricing — their ask, not the agreement</SectionNote>
                <HeaderBadge>this partnership</HeaderBadge>
            </SectionHeader>
            <ListCard>{rows}</ListCard>
        </Section>
    );
};

const LinkedShipperTariffs = ({ shipperGroups, defaultPricing, showsDefault }) => {
    const links = shipperTariffLinksFor(shipperGroups, defaultPricing);

    let rows = links.map((link) => {
        const pill = SOURCE_PILLS[link.source];
        const profileNames = link.profileNames.join(', ') || 'No delivery profile';
        const mapNote = link.onMap ? '' : ' · not on the map';

        return (
            <ListRow key={link.client_id}>
                <RowName>{link.business_name}</RowName>
                <RowDetail>
                    {link.detail} · {profileNames}
                    {mapNote}
                </RowDetail>
                <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
            </ListRow>
        );
    });
    if (links.length === 0) {
        rows = (
            <ListRow>
                <RowDetail>No org shippers are linked to this carrier yet.</RowDetail>
            </ListRow>
        );
    }

    return (
        <Section>
            <SectionHeader>
                <AccentBar />
                <SectionTitle>Linked shipper tariffs</SectionTitle>
                <SectionNote>
                    · the tariff that rates each shipper&apos;s orders.
                    {showsDefault && " A shipper's own tariff beats the default above."}
                </SectionNote>
                <HeaderBadge>corporate</HeaderBadge>
            </SectionHeader>
            <ListCard>{rows}</ListCard>
        </Section>
    );
};

const FlexCenterTariffs = ({ flexCenters }) => {
    let rows = flexCenters.map((flexCenter) => {
        const { detail, satisfied, statusLabel } = flexCenter.pricingCard;

        return (
            <ListRow key={flexCenter.client_id}>
                <RowName>{flexCenter.business_name}</RowName>
                <RowDetail>{detail}</RowDetail>
                <StatusPill tone={satisfied ? 'on' : 'bad'}>{statusLabel}</StatusPill>
            </ListRow>
        );
    });
    if (flexCenters.length === 0) {
        rows = (
            <ListRow>
                <RowDetail>Works under no Flex Center yet.</RowDetail>
            </ListRow>
        );
    }

    return (
        <Section>
            <SectionHeader>
                <AccentBar />
                <SectionTitle>Flex Center tariffs</SectionTitle>
                <HeaderBadge>corporate</HeaderBadge>
            </SectionHeader>
            <ListCard>{rows}</ListCard>
        </Section>
    );
};

// The carrier page passes the carrier's relationship, so a carrier the Carriers tab doesn't list still gets its tariffs.
const TariffLinks = ({ relationship }) => {
    const { client } = useContext(UserContext);
    const { rows, shipperGroupsByCarrier, flexCentersByContractor, defaultPricingByCarrier, loading, error } =
        useCarrierRows();

    if (error) return <EmptyState>Couldn&apos;t load tariff links.</EmptyState>;
    if (loading) return <EmptyState>Loading tariff links…</EmptyState>;

    let row = rows.find((carrierRow) => carrierRow.client_id === client.client_id);
    if (!row && relationship) row = unlistedCarrierRowFor(client, relationship);
    if (!row) return <EmptyState>This carrier isn&apos;t in your organization&apos;s network.</EmptyState>;

    const defaultPricing = defaultPricingByCarrier[row.client_id] || NO_DEFAULT_PRICING;
    const shipperGroups = shipperGroupsByCarrier[row.client_id] || [];
    const showsDefault = isFulfillmentPartnerRow(row) || row.isContractor;
    let partnerTariffs = (
        <LinkedShipperTariffs
            shipperGroups={shipperGroups}
            defaultPricing={defaultPricing}
            showsDefault={showsDefault}
        />
    );
    if (row.isContractor) {
        partnerTariffs = <FlexCenterTariffs flexCenters={flexCentersByContractor[row.client_id] || []} />;
    }

    return (
        <Panel>
            {showsDefault && <DefaultTariff row={row} defaultPricing={defaultPricing} />}
            <SelfServiceUploads />
            {partnerTariffs}
        </Panel>
    );
};

export default TariffLinks;
